/**
 * Run analysis: a pure function from a finished run to its `Report`
 * (docs/event-model.md §5–7, ADR-0013, ADR-0014). The UI calls it on its
 * store; golden tests call it headlessly.
 *
 * Pipeline: flows (flows.ts) → rules in registry order (rules/index.ts),
 * which add findings and escalate event severities → findings post-processing
 * (findings.ts) → capability summary, manifest, and coverage notes over the
 * escalated log.
 */
import { buildManifest } from "../manifest";
import { runRules } from "./apply";
import { summarizeCapabilities } from "./capabilities";
import { coverageNotes } from "./coverage";
import { finalizeFindings } from "./findings";
import { deriveFlows } from "./flows";
import { RULES } from "./rules";
import type { AnalysisInput, AnalysisRun, Report } from "./types";

export { applyEscalations, type RuleResults, runRules } from "./apply";
export { summarizeCapabilities } from "./capabilities";
export { coverageNotes } from "./coverage";
export { finalizeFindings } from "./findings";
export { deriveFlows } from "./flows";
export { RULES } from "./rules";
export * from "./types";

export function analyze(run: AnalysisRun): Report {
  const flows = deriveFlows(run.events, run.registry);
  const limitsHit = [...(run.limitsHit ?? [])];
  const input: AnalysisInput = {
    events: run.events,
    layers: run.layers,
    context: run.context,
    registry: run.registry,
    flows,
    globalDiff: run.globalDiff ?? [],
    limitsHit,
    ...(run.code !== undefined && { code: run.code }),
  };
  const { findings, escalations, events } = runRules(RULES, input);
  const finalInput = { ...input, events };
  const stats = run.stats ?? {
    events: run.events.length,
    virtualMs: run.events.reduce((max, e) => Math.max(max, e.t), 0),
    cpuMs: 0,
  };
  const finalFindings = finalizeFindings(findings);
  return {
    findings: finalFindings,
    flows,
    layers: [...run.layers],
    escalations,
    capabilities: summarizeCapabilities(events),
    manifest: buildManifest({ events, layers: run.layers, flows, context: run.context }),
    coverage: coverageNotes(finalInput, finalFindings),
    stats: { ...stats, limitsHit },
  };
}
